import { View, Text, StyleSheet, ScrollView, Platform, TouchableOpacity } from 'react-native';
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesome6 } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import CreateFeedLikeThisCard from './CreateFeedLikeThisCard';
import DigestIssueHeader from './DigestIssueHeader';
import DigestPaperList from './DigestPaperList';
import ShareDigestModal from './ShareDigestModal';
import MetaTags from '../MetaTags';
import Header from '../Header';
import Footer from '../Footer';
import Banner from '../ui/Banner';
import PrivateDigestAccessCard from './PrivateDigestAccessCard';
import { useAuth } from '../../lib/AuthContext';
import { useSession } from '../../lib/SessionContext';
import colors from '../../lib/colors';
import { getDigestUrl } from '../../lib/paperUrl';
import { useDigestQuery } from '../../lib/hooks/useDigestQuery';
import { useFontLoading } from '../../lib/FontContext';
import { interSemiBold } from '../../lib/fonts';
import { isPrivateOrNotFoundError } from '../../lib/accessErrors';
import { canAccessMainApp } from '../../utils/authHelpers';
import { capBillingPreviewPapers } from '../../lib/billingPreviewState';
import PreviewTrackingCard from '../billing/PreviewTrackingCard';
import PreviewHiddenMatchesNotice from '../billing/PreviewHiddenMatchesNotice';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { stripStaticPublicExportPrefix } from '../../lib/i18n/publicWeb';

// Add pulse animation for web
if (Platform.OS === 'web' && typeof document !== 'undefined') {
    const style = document.createElement('style');
    style.textContent = `
        @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
        }
        .skeleton-pulse {
            animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
    `;
    document.head.appendChild(style);
}

// Skeleton component for static rendering
function DigestIssueSkeleton() {
    return (
        <View style={styles.container}>
            <Header />
            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
            >
                <View style={styles.contentWrapper}>
                    <View style={styles.content}>
                        {/* Digest header skeleton */}
                        <View style={styles.headerSection}>
                            <View style={[styles.skeleton, { width: '70%', height: 32, marginBottom: 12 }]} />
                            <View style={[styles.skeleton, { width: 150, height: 20, marginBottom: 8 }]} />
                            <View style={[styles.skeleton, { width: 100, height: 20 }]} />
                        </View>

                        {/* Papers list skeleton */}
                        {[1, 2, 3, 4, 5].map((i) => (
                            <View key={i} style={styles.paperCardSkeleton}>
                                <View style={[styles.skeleton, { width: 40, height: 40, borderRadius: 20 }]} />
                                <View style={{ flex: 1, gap: 8 }}>
                                    <View style={[styles.skeleton, { width: '90%', height: 20 }]} />
                                    <View style={[styles.skeleton, { width: '70%', height: 16 }]} />
                                    <View style={[styles.skeleton, { width: '100%', height: 16 }]} />
                                    <View style={[styles.skeleton, { width: '80%', height: 16 }]} />
                                </View>
                            </View>
                        ))}
                    </View>
                </View>
                <Footer />
            </ScrollView>
        </View>
    );
}

interface DigestIssuePageContentProps {
    issueId: string;
    initialShowShareModal?: boolean;
    urlSlug?: string | null;
}

function normalizePathname(pathname: string): string {
    const normalized = pathname
        .replace(/\/+/g, '/')
        .replace(/\/+$/, '');
    return normalized || '/';
}

/**
 * Shared component for digest issue pages
 * Used by both /digest/[issueId] and /digest/[issueId]/[...slug]
 * Shows digest header and list of papers in the digest
 *
 * Note: Both routes are functionally identical - slug is cosmetic for SEO only
 */
export default function DigestIssuePageContent({
    issueId,
    initialShowShareModal,
    urlSlug,
}: DigestIssuePageContentProps) {
    // Hooks must be called before any conditional returns
    const router = useRouter();
    const { openAuth } = useAuth();
    const { session, isLoading: isSessionLoading } = useSession();
    const {
        data: digestIssue,
        isLoading,
        isFetching,
        error,
        refetch,
    } = useDigestQuery(issueId || '', urlSlug, {
        enabled: !isSessionLoading,
        viewerUserId: session?.user?.id,
    });
    const { fontsLoaded } = useFontLoading();
    const [showShareModal, setShowShareModal] = useState(false);
    const lastCanonicalRedirect = useRef<string | null>(null);
    const isOwner = Boolean(
        digestIssue
        && session?.user?.id
        && digestIssue.project.user_id === session.user.id
    );
    const shouldPresentPreviewDigest = digestIssue?.preview_access === 'restricted';

    // Get current URL for redirect after login
    const getCurrentUrl = () => {
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
            return window.location.pathname;
        }
        return `/digest/${issueId}`;
    };

    // Redirect to canonical URL if current URL doesn't match
    useEffect(() => {
        // Only canonicalize the legacy route (/digest/[issueId]).
        // The slug route is already canonical and should not navigate on mount.
        if (!digestIssue || Platform.OS !== 'web' || urlSlug) return;

        const canonicalPath = getDigestUrl(digestIssue);

        if (typeof window !== 'undefined') {
            const currentPath = stripStaticPublicExportPrefix(window.location.pathname);
            const currentSearch = window.location.search;
            const normalizedCurrentPath = normalizePathname(currentPath);
            const normalizedCanonicalPath = normalizePathname(canonicalPath);
            const redirectTarget = `${canonicalPath}${currentSearch}`;

            if (normalizedCurrentPath === normalizedCanonicalPath) {
                lastCanonicalRedirect.current = null;
                return;
            }

            if (lastCanonicalRedirect.current === redirectTarget) {
                return;
            }

            lastCanonicalRedirect.current = redirectTarget;
            router.replace(redirectTarget);
        }
    }, [digestIssue, router, urlSlug]);

    // Open share modal if initialShowShareModal is true and digest is loaded
    useEffect(() => {
        if (initialShowShareModal && digestIssue) {
            setShowShareModal(true);
        }
    }, [initialShowShareModal, digestIssue]);

    // Handle closing the share modal and cleaning up URL
    const handleCloseShareModal = () => {
        setShowShareModal(false);
        // Remove share param from URL without triggering navigation
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
            const url = new URL(window.location.href);
            url.searchParams.delete('share');
            url.searchParams.delete('utm_source');
            url.searchParams.delete('utm_medium');
            url.searchParams.delete('utm_content');
            window.history.replaceState({}, '', url.pathname);
        }
    };

    // Loading state - show skeleton
    if (
        isSessionLoading
        || isLoading
        || (isFetching && digestIssue?.preview_access !== 'restricted')
    ) {
        return <DigestIssueSkeleton />;
    }

    // Error state
    if (error || !digestIssue) {
        const isPrivateOrNotFound = isPrivateOrNotFoundError(error);

        const handleSignIn = () => {
            openAuth({ redirectTo: getCurrentUrl() });
        };

        const handleGoHome = () => {
            router.push('/');
        };

        // Non-private errors (generic error display)
        if (!isPrivateOrNotFound) {
            return (
                <View style={styles.container}>
                    <Header />
                    <View style={styles.errorContainer}>
                        <View style={[styles.errorIconContainer, styles.errorIconRed]}>
                            <FontAwesome6
                                name="circle-exclamation"
                                size={28}
                                color={colors.errorRed}
                            />
                        </View>
                        <Text style={[styles.errorTitle, fontsLoaded && interSemiBold]}><Trans id="digest.digestissuepagecontent.errorLoadingDigest" comment="UI copy in components/digest/DigestIssuePageContent.tsx">
                            Error Loading Digest
                        </Trans></Text>
                        <Text style={styles.errorText}>
                            {error?.message || <Trans id="digest.digestissuepagecontent.theDigestYouAreLookingForCouldNotBeFound" comment="Conditional UI copy in components/digest/DigestIssuePageContent.tsx">The digest you are looking for could not be found.</Trans>}
                        </Text>
                        <Text style={styles.supportText}><Trans id="digest.digestissuepagecontent.needHelpContactUsAtHelloPaperzillaAi" comment="UI copy in components/digest/DigestIssuePageContent.tsx">
                            Need help? Contact us at hello@paperzilla.ai
                        </Trans></Text>
                    </View>
                    <Footer />
                </View>
            );
        }

        // Private digest - different UI based on auth state
        return (
            <View style={styles.container}>
                <Header />
                <ScrollView
                    style={styles.scrollView}
                    contentContainerStyle={styles.privateScrollContent}
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.privateContentWrapper}>
                        <PrivateDigestAccessCard
                            isAuthenticated={!isSessionLoading && canAccessMainApp(session)}
                            onSignIn={handleSignIn}
                            onSwitchAccount={() => openAuth({ redirectTo: getCurrentUrl(), forSwitchAccount: true })}
                            fontsLoaded={fontsLoaded}
                        />

                        {/* Info card about Paperzilla */}
                        <View style={styles.infoCard}>
                            <View style={styles.infoHeader}>
                                <FontAwesome6 name="circle-info" size={16} color={colors.primaryGreen} />
                                <Text style={[styles.infoTitle, fontsLoaded && interSemiBold]}><Trans id="digest.digestissuepagecontent.whatIsPaperzilla" comment="UI copy in components/digest/DigestIssuePageContent.tsx">
                                    What is Paperzilla?
                                </Trans></Text>
                            </View>
                            <Text style={styles.infoText}><Trans id="digest.digestissuepagecontent.paperzillaWatchesNewPreprintsAndPubmedIndexedRecordsRanksWhatMatches" comment="UI copy in components/digest/DigestIssuePageContent.tsx">
                                Paperzilla watches new preprints and PubMed-indexed records, ranks what matches your interests, and delivers high-signal feeds you can skim in minutes through the app, RSS/Atom, email digest, or API.
                            </Trans></Text>
                            <TouchableOpacity
                                style={styles.learnMoreButton}
                                onPress={handleGoHome}
                                activeOpacity={0.8}
                            >
                                <Text style={[styles.learnMoreText, fontsLoaded && interSemiBold]}><Trans id="digest.digestissuepagecontent.learnMore" comment="UI copy in components/digest/DigestIssuePageContent.tsx">
                                    Learn more
                                </Trans></Text>
                                <FontAwesome6 name="arrow-right" size={12} color={colors.primaryGreen} />
                            </TouchableOpacity>
                        </View>

                        {/* Support text */}
                        <Text style={styles.supportText}><Trans id="digest.digestissuepagecontent.needHelpContactUsAtHelloPaperzillaAi" comment="UI copy in components/digest/DigestIssuePageContent.tsx">
                            Need help? Contact us at hello@paperzilla.ai
                        </Trans></Text>
                    </View>
                    <Footer />
                </ScrollView>
            </View>
        );
    }

    // Success state - render digest issue
    const issueDate = digestIssue.sent_at || digestIssue.created_at;
    const papersCount = digestIssue.papers.length;
    const totalFoundCount = digestIssue.papers_total_count ?? papersCount;
    const visiblePapers = shouldPresentPreviewDigest
        ? capBillingPreviewPapers(digestIssue.papers)
        : digestIssue.papers;
    const previewProjectId = digestIssue.project.id;
    const previewResultDigestId = digestIssue.id;
    const previewTrackingState = digestIssue.project.paused_at ? 'paused' : 'pausing';

    return (
        <>
            {/* SEO Meta Tags */}
            <MetaTags
                surfaceId="digest"
                canonicalPathname={getDigestUrl(digestIssue)}
                title={{
                    ...msg({
                        id: 'seo.digest.title',
                        message: '{projectName} - Paperzilla',
                        comment: 'Public digest metadata title. The project name is user content and must remain unchanged.',
                    }),
                    values: { projectName: digestIssue.project.name },
                }}
                description={{
                    ...msg({
                        id: 'seo.digest.description',
                        message: '{count, plural, one {Digest issue with # research paper} other {Digest issue with # research papers}}',
                        comment: 'Public digest metadata description.',
                    }),
                    values: { count: totalFoundCount },
                }}
                type="article"
            />

            <View style={styles.container}>
                <Header hidePreviewTrialBanner={shouldPresentPreviewDigest} />
                <ScrollView
                    style={styles.scrollView}
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.contentWrapper}>
                        <View style={styles.content}>
                            {/* Digest header */}
                            <DigestIssueHeader
                                digestName={digestIssue.project.name}
                                issueDate={issueDate}
                                papersCount={totalFoundCount}
                                status={digestIssue.status}
                                emailSent={digestIssue.email_sent}
                                projectId={digestIssue.project.id}
                                isOwner={isOwner}
                                isPublic={digestIssue.project.visibility === 'public'}
                                onSharePress={() => setShowShareModal(true)}
                            />

                            {digestIssue.project.visibility === 'public' && !isOwner && !isSessionLoading && !canAccessMainApp(session) && !shouldPresentPreviewDigest && (
                                <CreateFeedLikeThisCard
                                    projectId={digestIssue.project.id}
                                    entrySurface="digest_issue_template_card"
                                />
                            )}

                            {/* Fallback digest notice */}
                            {digestIssue.digest_type === 'fallback' && (
                                <View style={styles.fallbackBanner}>
                                    <Banner
                                        type="info"
                                        title={msg({ id: "digest.digestissuepagecontent.suggestedPapers", message: "Suggested Papers", comment: "UI title in components/digest/DigestIssuePageContent.tsx" })}
                                        message={msg({ id: "digest.digestissuepagecontent.noExactMatchesFoundInThePast7DaysHereAreSomeRelatedPapersBasedOnYourInterests", message: "No exact matches found in the past 7 days. Here are some related papers based on your interests.", comment: "UI message in components/digest/DigestIssuePageContent.tsx" })}
                                    />
                                </View>
                            )}

                            {shouldPresentPreviewDigest ? (
                                <PreviewTrackingCard
                                    projectName={digestIssue.project.name}
                                    previewProjectId={previewProjectId}
                                    resultDigestId={previewResultDigestId}
                                    trialHref={digestIssue.preview_trial_path}
                                    trackingState={previewTrackingState}
                                    style={styles.previewTrackingCard}
                                />
                            ) : null}

                            {/* Papers list */}
                            <DigestPaperList
                                papers={visiblePapers}
                                issueId={digestIssue.short_id || digestIssue.id}
                                projectName={digestIssue.project.name}
                            />

                            {shouldPresentPreviewDigest ? (
                                <PreviewHiddenMatchesNotice
                                    totalFoundCount={totalFoundCount}
                                    shownCount={visiblePapers.length}
                                    previewProjectId={previewProjectId}
                                    resultDigestId={previewResultDigestId}
                                    trialHref={digestIssue.preview_trial_path}
                                    style={styles.previewHiddenMatchesNotice}
                                />
                            ) : null}
                        </View>
                    </View>
                    <Footer />
                </ScrollView>
            </View>

            {/* Share Modal */}
            <ShareDigestModal
                visible={showShareModal}
                onClose={handleCloseShareModal}
                digestIssue={digestIssue}
                isOwner={isOwner}
                onVisibilityChanged={refetch}
            />
        </>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.bgWeak,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
    },
    contentWrapper: {
        alignItems: 'center',
    },
    content: {
        width: '100%',
        maxWidth: 900,
        paddingHorizontal: 20,
        paddingTop: 20,
        paddingBottom: 40,
        ...(Platform.OS === 'web' && {
            alignSelf: 'center',
        }),
    },
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 40,
        paddingVertical: 60,
    },
    errorTitle: {
        fontSize: 24,
        fontWeight: '600',
        color: colors.text900,
        marginBottom: 12,
        textAlign: 'center',
    },
    errorText: {
        fontSize: 16,
        color: colors.text700,
        textAlign: 'center',
        lineHeight: 24,
    },
    supportText: {
        fontSize: 14,
        color: colors.text500,
        textAlign: 'center',
        marginTop: 24,
        fontStyle: 'italic',
    },
    errorIconContainer: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: '#fef3c7',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 20,
    },
    errorIconRed: {
        backgroundColor: '#fee2e2',
    },
    // Skeleton styles
    skeleton: {
        backgroundColor: '#f3f4f6',
        borderRadius: 4,
        ...(Platform.OS === 'web' && {
            className: 'skeleton-pulse',
        }),
    },
    headerSection: {
        marginBottom: 24,
        paddingTop: 20,
    },
    paperCardSkeleton: {
        flexDirection: 'row',
        backgroundColor: colors.card,
        borderRadius: 12,
        padding: 16,
        borderWidth: 1,
        borderColor: colors.border,
        gap: 16,
        marginBottom: 16,
    },
    fallbackBanner: {
        marginBottom: 16,
    },
    previewTrackingCard: {
        marginBottom: 20,
    },
    previewHiddenMatchesNotice: {
        marginTop: 4,
        marginBottom: 20,
    },
    // Private digest page styles
    privateScrollContent: {
        flexGrow: 1,
    },
    privateContentWrapper: {
        flex: 1,
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 40,
        maxWidth: 480,
        alignSelf: 'center',
        width: '100%',
    },
    infoCard: {
        backgroundColor: colors.primary100,
        borderRadius: 12,
        padding: 20,
        width: '100%',
        marginTop: 20,
    },
    infoHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 10,
    },
    infoTitle: {
        fontSize: 14,
        fontWeight: '600',
        color: colors.primaryGreenDark,
    },
    infoText: {
        fontSize: 14,
        color: colors.text700,
        lineHeight: 21,
    },
    learnMoreButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginTop: 12,
    },
    learnMoreText: {
        fontSize: 14,
        fontWeight: '600',
        color: colors.primaryGreen,
    },
});
